import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { usePortalAuth } from '../../contexts/PortalAuthContext';
import { portalBrandingApi } from '../../services/portalApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { AlertCircle, Loader2, ArrowLeft, Lock } from 'lucide-react';
import { getApiErrorMessage } from '../../lib/apiError';
import { PortalCodeStep } from '../../components/portal/PortalCodeStep';

export default function PortalLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [branding, setBranding] = useState(null);
  const [mfa, setMfa] = useState(null);
  const { login, completeLogin } = usePortalAuth();
  const navigate = useNavigate();

  useEffect(() => {
    portalBrandingApi.get().then(({ data }) => setBranding(data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.mfa) setMfa(result.mfa);
      else navigate('/portal');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  const onVerified = (data) => { completeLogin(data, email); navigate('/portal'); };
  const onExpired = (message) => { setMfa(null); setPassword(''); setError(message); };

  return (
    <div className="min-h-screen bg-[#f8f9fb] dark:bg-[#0a0a0b] flex items-center justify-center p-4">
      <div className="w-full max-w-sm" data-testid="portal-login-page">
        {/* Brand */}
        <div className="text-center mb-8">
          {branding?.logoUrl ? (
            <img src={branding.logoUrl} alt={branding.companyName || 'Portal'} className="h-12 mx-auto mb-4 object-contain" />
          ) : (
            <div className="w-12 h-12 rounded-xl bg-black dark:bg-white mx-auto flex items-center justify-center mb-4">
              {/* Was a hardcoded "H" — our own initial, shown to every reseller's client
                  no matter what the branding says (v1.290.0 security pass). */}
              {branding?.companyName ? (
                <span className="text-white dark:text-black font-bold text-lg">
                  {branding.companyName.trim().charAt(0).toUpperCase()}
                </span>
              ) : (
                <Lock className="w-5 h-5 text-white dark:text-black" />
              )}
            </div>
          )}
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">{branding?.companyName || 'Client Portal'}</h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Sign in to your account</p>
        </div>

        {/* Login Card */}
        <div className="bg-white dark:bg-[#141417] border border-gray-200 dark:border-gray-800 rounded-xl p-6 space-y-5">
          {error && (
            <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/20 rounded-lg p-3" data-testid="portal-login-error">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {mfa ? <PortalCodeStep mfa={mfa} onVerified={onVerified} onExpired={onExpired} /> : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                data-testid="portal-login-email"
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                <Link to="/portal/forgot-password" className="text-xs text-gray-500 hover:text-gray-900 dark:hover:text-white">
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                data-testid="portal-login-password"
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="portal-login-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Sign In
            </Button>
          </form>
          )}
        </div>

        <div className="text-center mt-4">
          <Link to="/login" className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" />
            Admin login
          </Link>
        </div>
      </div>
    </div>
  );
}
